import React, {useState, useCallback} from 'react';
import {useAuth} from '../hooks/useAuth';
import {useError} from '../../../Presentation/contexts/ErrorContext';
import {DeviceInfoService} from '../../../Services/device/DeviceInfoService';
import {useNotification} from '../../../Services/useNotification';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {AppTitle, AppSubtitle} from '../../../Presentation/components/ui/Typography';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {AppError, ErrorType} from '../../../Data/config/AppError';
import {checkAuthStatus} from '../../../utils/authUtils';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';
import {useClientProfile} from '../../../core/context/ClientContext';

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const {login, loading: isLoading} = useAuth();
  const profile = useClientProfile();
  const {showError} = useError();
  const {success: showSuccess} = useNotification();
  const {t} = useTranslation();
  const navigate = useNavigate();

  const handleLogin = useCallback(async () => {
    try {
      // منع التكرار إذا كان تسجيل الدخول يحدث بالفعل
      if (isLoading) {
        return;
      }

      if (!username || !password) {
        showError(new AppError(
          t('login.missing_fields'),
          ErrorType.VALIDATION
        ));
        return;
      }

      // Loading state is now managed by the auth store

      const deviceInfo = await DeviceInfoService.getInstance().getDeviceInfo();
      const result = await login({
        username,
        password,
        device_info: deviceInfo
      });

      if (result.success) {
        if (result.message) {
          showSuccess(result.message);
        }

        // Check auth status after successful login
        checkAuthStatus();

        // Add a small delay to ensure token is stored before navigation
        const isProvider = AuthorizationService.getInstance().isServiceProvider(result.user || null);

        setTimeout(() => {
          // Redirect based on user role
          if (isProvider) {
            navigate('/provider', {replace: true});
          } else {
            navigate('/dashboard', {replace: true});
          }
        }, 200);
      } else {
        // إذا فشل تسجيل الدخول، عرض رسالة الخطأ
        showError(new AppError(
          result.error || 'فشل تسجيل الدخول',
          ErrorType.AUTHENTICATION
        ));
      }
    } catch (error: unknown) {
      if (error instanceof AppError) {
        showError(error);
      } else {
        showError(new AppError(
          t('login.error'),
          ErrorType.UNKNOWN
        ));
      }
    } finally {
      // Loading state is automatically managed by the auth store
    }
  }, [username, password, isLoading, login, showError, t, navigate]);

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-sans" dir="rtl">
      {/* Left: Login Form */}
      <div className="w-full md:w-2/5 flex items-center justify-center bg-white px-6 py-12 md:py-0">
        <div className="w-full max-w-md">
          <AppTitle className="block text-center mb-2">{t('login.title')}</AppTitle>
          <AppSubtitle className="block text-center mb-8">{t('login.subtitle')}</AppSubtitle>
          <form className="w-full space-y-6" onSubmit={(e) => {
            e.preventDefault();
            if (!isLoading) {
              handleLogin();
            }
          }}>
            <div>
              <label htmlFor="username" className="block font-medium text-gray-700 mb-1 text-right">{t('login.username')}</label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="block w-full rounded-2xl border border-gray-300 px-4 py-3 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary sm:text-base bg-white"
                placeholder="stc.channel"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                dir="rtl"
                style={{fontFamily: 'inherit'}}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1 text-right">{t('login.password')}</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="block w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary sm:text-base bg-white"
                  placeholder="************"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  dir="rtl"
                  style={{fontFamily: 'inherit'}}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                  aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <span className="text-sm text-gray-500">{t('login.forgot_password')}</span>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-2xl text-white text-lg font-bold transition focus:outline-none ${
                isLoading ? 'bg-primary/50' : 'bg-primary hover:bg-primary/90'
              }`}
              style={{fontFamily: 'inherit'}}
            >
              {isLoading ? (
                <Spinner size={20} color="#fff" center />
              ) : (
                t('login.login_button')
              )}
            </button>
          </form>
        </div>
      </div>
      {/* Right: Background Image with optional client logo overlay */}
      <div className="w-full md:w-3/5 relative flex items-center justify-center min-h-[300px] md:min-h-0" style={{minHeight: '100vh'}}>
        <img
          src={profile.theme.loginBackgroundUrl ?? '/assets/background_login.png'}
          alt="login background"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {profile.theme.loginLogoUrl && (
          <img
            src={profile.theme.loginLogoUrl}
            alt={profile.theme.brandName}
            className="relative z-10 w-56 md:w-72 drop-shadow-lg"
          />
        )}
      </div>
    </div>
  );
};

export default LoginScreen;
